import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { setContext } from '@apollo/client/link/context'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'

import Swal from 'sweetalert2'
import { UnauthenticatedUserRoutes } from '../routes/unauthenticated-user-routes'
import { cacheConfig } from '@/utils/apollo-cache-config'

const UNAUTHENTICATED_PATHS = UnauthenticatedUserRoutes.map((route) => `/${route.path}`.replace('//', '/'))
const GRAPHQL_URI = import.meta.env.VITE_GRAPHQL_URI || 'http://localhost:4000/graphql'
const GRAPHQL_WS_URI = import.meta.env.VITE_GRAPHQL_WS_URI || 'ws://localhost:4000/graphql'

// WebSocket client instance
let wsClient = null

const createWsClient = () => {
    if (wsClient) {
        wsClient.dispose()
    }

    wsClient = createClient({
        url: GRAPHQL_WS_URI,
        connectionParams: () => ({
            authorization: localStorage.getItem('token') || '',
        }),
        on: {
            connected: (socket, payload) => {
                // Handle token refresh during WebSocket connection
                if (payload?.newToken) {
                    console.log('WebSocket: Received new access token, updating localStorage')
                    localStorage.setItem('token', payload.newToken)
                }
            },
        },
        retryAttempts: 3,
        retryWait: (retryCount) => Math.min(1000 * 2 ** retryCount, 30000),
    })

    return wsClient
}

// Initialize WebSocket client
createWsClient()

// WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(wsClient)

const authLink = setContext((_, { headers }) => {
    return {
        headers: {
            ...headers,
            authorization: localStorage.getItem('token') || '',
        },
    }
})

/*
 * Middleware to Intercept GraphQL Errors
 * The UNAUTHENTICATED error from an API request on an Authenticated Route.
 * will cause a logout and nav to login page
 */
const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors) {
        const unAuth = graphQLErrors.some(
            (err) =>
                String(err.extensions.code).toLowerCase() === 'unauthenticated' ||
                String(err.message).toLowerCase() === 'unauthenticated',
        ) // neo4jGraphql sends the incorrect code with a message for unauth.
        if (unAuth) {
            const requestPath = window.location.pathname

            if (UNAUTHENTICATED_PATHS.includes(requestPath)) {
                // ignore
                return
            }

            console.log(`Apollo Client received UnAuth logging out user: ${localStorage.getItem('token')}`)
            localStorage.removeItem('token')
            client?.clearStore()

            Swal.fire({
                title: 'Session has expired',
                text: 'Click OK button to be redirected to the login page.',
                icon: 'warning',
                backdrop: `rgba(60, 60, 60, 0.7)`,
                confirmButtonText: 'OK',
                allowOutsideClick: false,
            }).then(() => (window.location.href = '/login'))
        }
    }
})

// Middleware to Intercept Responses & update access token
const responseInterceptor = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
        const context = operation.getContext()
        const newToken = context.response?.headers.get('X-Turbamedia-Token')

        if (newToken) {
            localStorage.setItem('token', newToken)
        }

        return response
    })
})

const uploadLink = createUploadLink({
    uri: GRAPHQL_URI,
    credentials: 'include', // http-only cookie that carries the refresh token
})

// Split links based on operation type
const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query)
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    wsLink,
    ApolloLink.from([authLink, responseInterceptor, errorLink, uploadLink]),
)

const client = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(cacheConfig),
})

// Export function to refresh WebSocket connection after logout
export const refreshWebSocketConnection = () => {
    createWsClient()
}

export default client
